import {
  BdGraphNode,
  BdGraph,
  BdGraphNodeStateMap,
  DoFunc,
  BdGraphNodeFactory,
  BdGraphFactory,
} from './graph.model';
import { ShowObjects } from 'src/app/engine/models/show-objects.model';

export namespace GraphUtils {
  export function addRootNode<TNode extends BdGraphNode>(
    graph: BdGraph<TNode>,
    nodeData: TNode,
  ): void {
    addNode(graph, nodeData);
    const root: TNode = getNode(graph, nodeData.id);
    graph.roots[root.id] = root;
  }

  export function getNodes<TNode extends BdGraphNode>(
    graph: BdGraph<TNode>,
    targets: string[],
  ): TNode[] {
    const nodes: TNode[] = [];
    targets.forEach((id) => {
      if (graph.nodes[id]) {
        nodes.push(graph.nodes[id]);
      }
    });
    return nodes;
  }

  export function getAncestors<TNode extends BdGraphNode>(
    graph: BdGraph<TNode>,
    id: string,
    inclusive: boolean = false,
    order: string = 'ascending',
  ): string[] {
    const ancestors = inclusive ? [id] : [];

    // walk up the tree
    let _parentId = graph.predecessors[id];
    while (_parentId) {
      ancestors.push(_parentId);
      _parentId = graph.predecessors[_parentId];
    }

    // respect user-specified order.
    // NOTE: reverse() is relatively slow, but for the modest tree-depths we expect (tens rather than hundreds), it should be sufficient.
    if (order === 'descending') {
      ancestors.reverse();
    }

    return ancestors;
  }

  export function addNode<TNode extends BdGraphNode>(graph: BdGraph<TNode>, nodeData: TNode): void {
    if (!graph.nodes[nodeData.id]) {
      graph.nodes[nodeData.id] = nodeData;
    }
  }

  // get node data if it exists, otherwise return null
  export function getNode<TNode extends BdGraphNode>(graph: BdGraph<TNode>, id: string): TNode {
    return graph.nodes[id] || null;
  }

  // get nearest ancestor if it exists, otherwise return null
  // this is a (private) utility
  export function getAncestor<TNode extends BdGraphNode>(
    graph: BdGraph<TNode>,
    id: string,
  ): string {
    return graph.predecessors[id] || null;
  }

  // union of all immediate ancestors of a set of nodes
  export function getAllAncestorsFromList<TNode extends BdGraphNode>(
    graph: BdGraph<TNode>,
    targets: string[],
  ): string[] {
    const ancestors = {};
    let _ancestor: string;
    targets.forEach((id) => {
      _ancestor = getAncestor(graph, id);
      if (_ancestor && !ancestors[_ancestor]) {
        ancestors[_ancestor] = true;
      }
    });
    return Object.keys(ancestors);
  }

  // return all child node ids of target node
  export function getSuccessors<TNode extends BdGraphNode>(
    graph: BdGraph<TNode>,
    id: string,
  ): string[] {
    return [...(graph.successors[id] || [])];
  }

  // get graph node state matching properties
  export function getState<TNode extends BdGraphNode>(
    graph: BdGraph<TNode>,
    id: string,
    properties: string[],
  ): BdGraphNodeStateMap {
    const state = {};
    const node = getNode(graph, id);
    Object.entries(node.boolState).forEach(([property, value]) => {
      if (properties && properties.includes(property)) {
        state[property] = value;
      }
    });

    Object.entries(node.numState).forEach(([property, value]) => {
      if (properties && properties.includes(property)) {
        state[property] = value;
      }
    });

    return state;
  }

  // return true if node is a leaf node
  export function isLeaf<TNode extends BdGraphNode>(graph: BdGraph<TNode>, id: string): boolean {
    return !!graph.nodes[id] && graph.successors[id] === undefined;
  }

  // return true if node is a root node
  export function isRoot<TNode extends BdGraphNode>(graph: BdGraph<TNode>, id: string): boolean {
    return graph.roots.hasOwnProperty(id) || false;
  }

  // return all root-node ids
  export function getRoots<TNode extends BdGraphNode>(graph: BdGraph<TNode>): string[] {
    return Object.keys(graph.roots);
  }

  function setNodePropertiesFromObject<TNode extends BdGraphNode>(
    node: TNode,
    srcObject: any,
    props: Object,
    idKey: string = 'id',
  ) {
    Object.entries(props).forEach(([k, v]) => {
      let val: any;
      // get value (from string or function)
      if (typeof v === 'string') {
        val = srcObject[v];
      } else if (typeof v === 'function') {
        val = v(srcObject[idKey]);
      }

      // store as boolean or numerical state property
      if (typeof val === 'boolean') {
        node.boolState[k] = val;
      } else if (typeof val === 'number') {
        node.numState[k] = val;
      } else {
        node[k] = val;
      }
    });
  }

  // recursively build graphNode from nested element
  function scrapeDataNode<TNode extends BdGraphNode>(
    graph: BdGraph<TNode>,
    parentId: string,
    nodeData: Object,
    idKey: string,
    childKey: string,
    props: Object,
    depth: number,
  ): void {
    // create graph node as specified generic type node
    const graphNode = BdGraphNodeFactory(nodeData[idKey]) as TNode;

    // set Depth
    graphNode.depth = depth;

    // collect properties
    setNodePropertiesFromObject(graphNode, nodeData, props, idKey);

    // add node and relationships to graph
    graph.nodes[graphNode.id] = graphNode;

    if (parentId) {
      graph.successors[parentId] = [...(graph.successors[parentId] || []), graphNode.id];
      graph.predecessors[graphNode.id] = parentId;
    } else {
      graph.roots[graphNode.id] = graphNode;
    }

    // recurse
    depth += 1;

    const children = nodeData[childKey] || [];

    children.forEach((childData: Object) => {
      scrapeDataNode(graph, graphNode.id, childData, idKey, childKey, props, depth);
    });
  }

  // depth first traversal and do
  // this is a (private) utility
  export function depthFirst<TNode extends BdGraphNode>(
    graph: BdGraph<TNode>,
    doFunc: DoFunc,
    id?: string,
  ): void {
    if (!id) {
      // start at the top fo the graph and operate on all roots
      getRoots(graph).forEach((rid) => {
        doFunc(rid);
        depthFirst(graph, doFunc, rid);
      });
    } else {
      // act on node and keep going down
      doFunc(id);
      getSuccessors(graph, id).forEach((cid) => {
        depthFirst(graph, doFunc, cid);
      });
    }
  }

  // factory for new BdGraph nested objects
  const DEFAULT_ID_KEY = 'objectId';
  const DEFAULT_CHILDREN_KEY = 'objects';

  export function createGraphFromSrc<TNode extends BdGraphNode>(
    srcData: Object[],
    idKey: string = DEFAULT_ID_KEY,
    childKey: string = DEFAULT_CHILDREN_KEY,
    nodeProperties: Object = {},
  ): BdGraph<TNode> {
    const depth = 0; // depth of root nodes is 0
    const graph = BdGraphFactory<TNode>();
    srcData.forEach((nodeData: Object) => {
      scrapeDataNode<TNode>(graph, null, nodeData, idKey, childKey, nodeProperties, depth);
    });

    return graph;
  }

  // In this function, we are pruning rootObjects according to showObjects

  // when do we preserve a branch?
  // -> when showObjects for the node is true
  // -> when showObjects for the node is undefined, and showObjects for it's ancestor is true

  // when do we prune a branch?
  // -> when showObjects for the node is false
  // -> when showObjects for the node is undefined, and showObjects for it's ancestor is false

  export interface SGNode {
    objectId: string;
    objects: SGNode[];
  }

  export function pruneHiddenObjects(
    sceneGraph: SGNode[],
    showObjects: ShowObjects,
    ancestorShowObjectValue?,
  ): SGNode[] {
    const filteredSceneGraph = [] as SGNode[];

    for (const node of sceneGraph) {
      const showValue = showObjects[node.objectId];

      // use show objects to see whether or not we preserve this node
      // when show objects for this node is undefined, fall back to the ancestor's show object value
      const shouldPreserveNode = (() => {
        if (showValue === true) {
          return true;
        }
        if (showValue === false) {
          return false;
        }
        return ancestorShowObjectValue;
      })();

      // recurse for all children
      const filteredChildren = pruneHiddenObjects(
        node.objects || [],
        showObjects,
        shouldPreserveNode,
      );

      if (shouldPreserveNode) {
        // preserve the node and its children, if any
        const nodeCopy = { ...node };
        nodeCopy.objects = filteredChildren;
        filteredSceneGraph.push(nodeCopy);
      } else {
        // throw out the node, and only save its children, if any
        filteredChildren.forEach((filtered) => filteredSceneGraph.push(filtered));
      }
    }

    return filteredSceneGraph;
  }
}
